import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import {
  useBillingReconciliationCredits,
} from '../../hooks/api/useBillingReconciliationHooks.js';
import { signedMoney } from './format.js';
import { EMPTY_CREDIT_ROWS } from './manualCreditFormModel.js';
import { useManualCreditForm } from './useManualCreditForm.js';
import VarianceEvidencePicker from './VarianceEvidencePicker.jsx';

const EMPTY_CREDITS = [];

const AddCreditDialog = ({ open, onClose, runId, program, serviceMonth }) => {
  const form = useManualCreditForm({ open, onClose, runId, program, serviceMonth });

  return (
    <Dialog open={open} onClose={form.createCredit.isPending ? undefined : form.close} fullWidth maxWidth="md">
      <DialogTitle>Add credit</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {form.error ? <Alert severity="error">{form.error}</Alert> : null}
          <Autocomplete
            options={form.accountOptions}
            loading={form.accountsQuery.isLoading}
            value={form.selectedAccount}
            inputValue={form.accountSearch}
            onInputChange={(_event, value, reason) => {
              if (reason === 'reset') {
                form.setAccountSearch(value || form.selectedAccount?.label || '');
                return;
              }
              if (reason === 'clear') {
                form.selectAccount(null);
                return;
              }
              form.setAccountSearch(value);
            }}
            onChange={(_event, value) => form.selectAccount(value)}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, value) => (
              option?.bac === value?.bac
              && option?.salesforceAccountId === value?.salesforceAccountId
            )}
            filterOptions={(options, state) => options.filter((option) => {
              const haystack = `${option.bac} ${option.dealerName} ${option.salesforceAccountName}`.toLowerCase();
              return haystack.includes(state.inputValue.toLowerCase());
            })}
            renderInput={(params) => (
              <TextField {...params} label="BAC or Salesforce account" placeholder="Search live AppSheet accounts by BAC, dealer, or account name" />
            )}
          />

          {form.selectedAccount ? (
            <Stack spacing={1}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Chip size="small" label={`BAC ${form.selectedAccount.bac}`} color="info" />
                <Autocomplete
                  size="small"
                  options={form.accountChoices}
                  loading={form.bacAccountsQuery.isLoading}
                  value={form.selectedAccount}
                  onChange={(_event, value) => {
                    if (value) {
                      form.selectAccount(value);
                    }
                  }}
                  getOptionLabel={(option) => option?.salesforceAccountName || option?.dealerName || option?.label || ''}
                  isOptionEqualToValue={(option, value) => (
                    option?.bac === value?.bac
                    && option?.salesforceAccountId === value?.salesforceAccountId
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Salesforce account" />
                  )}
                  sx={{ minWidth: { md: 360 }, flex: 1 }}
                />
              </Stack>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Pick a product to inherit</Typography>
              {form.evidenceQuery.isLoading ? (
                <Skeleton height={56} />
              ) : (
                <VarianceEvidencePicker
                  gmItems={form.evidenceQuery.data?.gmRows || EMPTY_CREDIT_ROWS}
                  cpqItems={form.evidenceQuery.data?.cpqRows || EMPTY_CREDIT_ROWS}
                  dpeItems={form.evidenceQuery.data?.dpeData?.products || EMPTY_CREDIT_ROWS}
                  appsheetData={form.evidenceQuery.data?.appsheetData || null}
                  selectedEvidence={form.selectedProduct}
                  currentOrderItemId={form.selectedProduct?.orderItemId || ''}
                  currentReferenceId={form.selectedProduct?.referenceId || ''}
                  onSelectEvidence={form.selectProduct}
                  maxListHeight={320}
                  pageSize={60}
                />
              )}
            </Stack>
          ) : null}

          <Divider />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <Autocomplete
              freeSolo
              options={form.catalogOptions}
              loading={form.catalogQuery.isLoading}
              value={form.catalogOptions.find((option) => option.code === form.customProductCode) || form.customProductCode}
              onChange={(_event, value) => form.selectCatalogProduct(value)}
              inputValue={form.customProductCode}
              onInputChange={(_event, value) => form.updateCustomProductCode(value)}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option?.code || '')}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return <li key={key} {...optionProps}>{option.label}</li>;
              }}
              renderInput={(params) => (
                <TextField {...params} label="Product code" placeholder="Type a product code or choose from pricing catalog" />
              )}
              fullWidth
            />
            <TextField
              label="Product price"
              value={form.customPrice}
              onChange={(event) => form.setCustomPrice(event.target.value)}
              fullWidth
              inputMode="decimal"
            />
            <TextField
              label="Full credit amount"
              value={form.fullCreditAmount}
              onChange={(event) => form.setFullCreditAmount(event.target.value)}
              fullWidth
              required
              inputMode="decimal"
            />
          </Stack>
          <TextField
            label="Notes"
            value={form.notes}
            onChange={(event) => form.setNotes(event.target.value)}
            minRows={3}
            multiline
            placeholder="Internal note for this manual credit"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={form.close} disabled={form.createCredit.isPending}>Cancel</Button>
        <Button variant="contained" onClick={form.submit} disabled={form.createCredit.isPending || !form.canSubmit}>
          Add credit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CreditsAppliedPanel = ({ runId, program = 'website', serviceMonth = null, limit = 500, onOpenBac = null }) => {
  const query = useBillingReconciliationCredits({ runId, limit });
  const [dialogOpen, setDialogOpen] = useState(false);
  const credits = query.data || EMPTY_CREDITS;
  const total = useMemo(
    () => credits.reduce((sum, credit) => sum + Number(credit.amount || 0), 0),
    [credits],
  );
  const hasCredits = credits.length > 0;

  return (
    <Collapse in timeout={180}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ReceiptLongOutlinedIcon color="info" />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Credits Applied
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Credit rows surfaced for visibility and included in modified totals
              </Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Add credit
            </Button>
            <Chip size="small" color="info" variant="outlined" label={`${credits.length} rows`} sx={{ fontWeight: 700 }} />
            <Chip size="small" color="info" label={signedMoney(total)} sx={{ fontWeight: 800 }} />
          </Stack>

          {query.isLoading ? (
            <Skeleton height={42} />
          ) : query.isError ? (
            <Alert severity="error" onClose={() => query.refetch()}>
              Credits could not be loaded.
            </Alert>
          ) : hasCredits ? (
            <Stack spacing={0.75}>
              {credits.map((credit) => (
                <Stack
                  key={credit.id}
                  role={onOpenBac && credit.bac ? 'button' : undefined}
                  tabIndex={onOpenBac && credit.bac ? 0 : undefined}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  onClick={() => credit.bac && onOpenBac?.(credit.bac, {
                    title: `Credit row - BAC ${credit.bac}`,
                    severity: 'info',
                    dealerName: credit.dealerName || '',
                    summary: `${signedMoney(Number(credit.amount || 0))} credit for ${credit.productCode || 'product'}`,
                    detail: credit.invoiceNumber ? `Invoice ${credit.invoiceNumber}` : 'CPQ credit row',
                  })}
                  onKeyDown={(event) => {
                    if (!onOpenBac || !credit.bac || (event.key !== 'Enter' && event.key !== ' ')) return;
                    event.preventDefault();
                    onOpenBac(credit.bac, {
                      title: `Credit row - BAC ${credit.bac}`,
                      severity: 'info',
                      dealerName: credit.dealerName || '',
                      summary: `${signedMoney(Number(credit.amount || 0))} credit for ${credit.productCode || 'product'}`,
                      detail: credit.invoiceNumber ? `Invoice ${credit.invoiceNumber}` : 'CPQ credit row',
                    });
                  }}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    cursor: onOpenBac && credit.bac ? 'pointer' : 'default',
                    '&:hover': onOpenBac && credit.bac ? { outline: '1px solid', outlineColor: 'primary.main' } : undefined,
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Box sx={{ minWidth: 110 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {signedMoney(Number(credit.amount || 0))}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {credit.serviceMonth || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      BAC {credit.bac} {credit.dealerName ? `- ${credit.dealerName}` : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {credit.productCode || 'Product'} {credit.invoiceNumber ? `- ${credit.invoiceNumber}` : ''}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          ) : null}
        </Stack>
      </Paper>
      <AddCreditDialog open={dialogOpen} onClose={() => setDialogOpen(false)} runId={runId} program={program} serviceMonth={serviceMonth} />
    </Collapse>
  );
};

export default CreditsAppliedPanel;
